import type { AlertSeverity, AlertSummary, JsonBlob } from '@regie/api-contract'
import { formatNumber } from '@/lib/formatters'

/**
 * Tone of the global alert signal (nav badge + banner): rust as soon as one
 * open alert is critical, brass otherwise; null when nothing is open (or the
 * summary is unavailable), meaning "render nothing".
 */
export function summaryTone(summary: AlertSummary | null | undefined): 'danger' | 'warning' | null {
  if (!summary || summary.open <= 0) return null
  return summary.critical > 0 ? 'danger' : 'warning'
}

/** Row severity → status tone: critical is rust, warning is brass. */
export function severityTone(severity: AlertSeverity): 'danger' | 'warning' {
  return severity === 'critical' ? 'danger' : 'warning'
}

export interface DetailEntry {
  key: string
  value: string
}

/**
 * Flattens the property-reported `details` object into compact `key: value`
 * pairs. Numbers are fr-FR formatted (3218 → "3 218"), nested values are
 * serialized as JSON so nothing is silently dropped.
 */
export function detailEntries(details: JsonBlob | null | undefined): DetailEntry[] {
  if (!details) return []
  return Object.entries(details).map(([key, raw]) => ({ key, value: formatDetailValue(raw) }))
}

function formatDetailValue(raw: unknown): string {
  if (raw === null || raw === undefined) return '—'
  if (typeof raw === 'number') return formatNumber(raw)
  if (typeof raw === 'string') return raw
  if (typeof raw === 'boolean') return String(raw)
  return JSON.stringify(raw)
}
